import React, { useEffect, useState, useRef } from "react";
import "./App.css";

import diagonal from "./images/hertz-hero-desktop-mask-bg.png";

import Loading from "./components/Loading";
import Map from "./components/Map";
import BlackoutDates from "./components/BlackoutDates";
import Dropdown from "./components/Dropdown";
import ErrorMessages from "./components/Error";

const LOCATION_SEARCH_API = process.env.REACT_APP_LOCATION_SEARCH_API;
const BLACKOUT_DATES_API_KEY = process.env.REACT_APP_BLACKOUT_DATES_API_KEY;
const BLACKOUT_DATES_API = process.env.REACT_APP_BLACKOUT_DATES_API;

const getYear = () => {
  return new Date().getFullYear();
};

function App() {
  const [search, setSearch] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [raField, setRaField] = useState({ borderBottom: "none" });
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState(search);
  const abortControllerRef = useRef(null);

  const checkIfEnter = (e) => {
    if (e.keyCode === 13) {
      const inputText = e.target.value;
      setSearch(inputText);
      if (inputText.length > 1 && !loading) {
        findLocations(inputText);
      }
    }
  };

  const findLocations = async (location, signal) => {
    try {
      setLoading(true);
      setErrorMessage("");
      setSearchResults([]);
      setSelectedLocation(null);
      setRaField({ borderBottom: "none" });
      const url = encodeURI(`${LOCATION_SEARCH_API}/hertz?search=${location}`);
      const resp = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        signal: signal,
      });
      let jsonStr = await resp.json();

      if (jsonStr?.data?.length === 0 || resp.status === 404) {
        setErrorMessage("No locations found. Please try again.");
        setRaField({ borderBottom: "3px solid #DC143C" });
        setLoading(false);
        return;
      }

      if (resp.status !== 200) {
        setErrorMessage("An error occurred. Please try again.");
        setRaField({ borderBottom: "3px solid #DC143C" });
        setLoading(false);
        return;
      }

      const body = [];

      jsonStr.data.forEach((location) => {
        body.push({
          locationName: location.name,
          address: location.address?.address1,
          city: location.address?.city || "",
          state: location.address?.state_long || "",
          zip: location.address?.postal_code || "",
          latitude: location.address?.latitude,
          longitude: location.address?.longitude,
          phone: location.contact_info?.phone?.national_phone_number,
          hours: location.curated_days,
          blackoutDates: [],
          roomNumber: location.address?.address2 || "",
          legacyOwningArea: location?.legacy_owning_area || null,
        });
      });

      setErrorMessage("");
      setRaField({ borderBottom: "none" });
      setSearchResults(body);
      setLoading(false);
      return;
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      } else {
        setErrorMessage("An error occurred. Please try again.");
        setRaField({ borderBottom: "3px solid #DC143C" });
        setLoading(false);
        return;
      }
    }
  };

  const getBlackoutDates = async (location) => {
    try {
      setLoading(true);
      setSearchResults([]);
      if (!location.legacyOwningArea) {
        setSelectedLocation(location);
        setLoading(false);
      }

      const url = encodeURI(
        `${BLACKOUT_DATES_API}?location=${location.legacyOwningArea}`
      );

      const resp = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": BLACKOUT_DATES_API_KEY,
        },
      });
      let jsonStr = await resp.json();

      if (resp.status !== 200) {
        setSelectedLocation(location);
        setLoading(false);
        return;
      }

      if (jsonStr.success === false) {
        setSelectedLocation(location);
        setLoading(false);
        return;
      }

      if (jsonStr.success === true) {
        setSelectedLocation({
          ...location,
          blackoutDates: jsonStr.blackoutDates,
        });
        setLoading(false);
      }
    } catch (error) {
      setErrorMessage("An error occurred. Please try again.");
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(search);
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (!debouncedQuery) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    findLocations(debouncedQuery, signal);

    return () => {
      abortControllerRef.current.abort();
    };
  }, [debouncedQuery]);

  useEffect(() => {
    if (selectedLocation) {
      sessionStorage.setItem("location", JSON.stringify(selectedLocation));
    }
  }, [selectedLocation]);

  useEffect(() => {
    const location = sessionStorage.getItem("location");
    if (location) {
      setSelectedLocation(JSON.parse(location));
    }
  }, []);

  return (
    <div className="App">
      <div>
        <div id="page">
          <div className="hle-header">
            <div className="hle-header-row">
              <div className="header-col header-col-right">
                <img src={diagonal} className="diagonalImg" alt="" />
              </div>
              <div className="header-col header-col-left">
                <div className="header-col-left-inner">
                  <div className="header-col-left-max-width">
                    <h1 className="hero-h1">
                    {getYear()} Peak Dates
                    </h1>
                    <span className="hero-span">
                      Redeem free rental days, even during peak rental times.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* </div> */}

          <div id="res-home-page">
            <section className="res-form">
              <div className="res-otr-wrp">
                <div className="res-inr-wrp">
                  <div className="res-pos-rel">
                    <div>
                      <div className="extension-header">
                      Find Peak Dates
                      </div>
                      <div className="directions">
                        <p>
                        Free rental day rewards can be redeemed during peak rental periods. If a free rental day falls on a peak rental day, it will be available for booking with an additional points redemption.
                        </p>
                      </div>
                    </div>
                    <div className="inputSection">
                      <div className="columns col-left">
                        <div className="input-group">
                          <div>
                            <label
                              htmlFor="rentalAgreementNumber"
                              className="label"
                            >
                              Location
                            </label>
                          </div>
                          <br />
                          <div style={raField}>
                            <input
                              type="text"
                              name="rentalAgreementNumber"
                              ref={(input) => input && input.focus()}
                              onKeyDown={checkIfEnter}
                              value={search}
                              data-size="30"
                              data-maxlength="25"
                              placeholder="Enter Location"
                              onChange={(e) => {
                                const searchText = e.target.value;
                                setSearch(searchText);
                              }}
                            ></input>
                          </div>
                        </div>
                      </div>
                    </div>
                    {!loading && searchResults.length ? (
                      <Dropdown
                        locations={searchResults}
                        getBlackoutDates={getBlackoutDates}
                      />
                    ) : null}

                    {loading && <Loading />}

                    {!loading && errorMessage.length > 0 && (
                      <ErrorMessages
                        width={"50%"}
                        errorMessage={errorMessage}
                      />
                    )}
                    <div id="lookupBtn">
                      <button
                        className={
                          search.length < 2 || loading
                            ? "default disabled"
                            : "default primary"
                        }
                        id="extendBtn"
                        disabled={loading || search.length < 2}
                        onClick={() => {
                          findLocations(search);
                          sessionStorage.setItem("search", search);
                        }}
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {selectedLocation ? (
              <div>
                <div className="content">
                  <div className="date-left-col">
                    <div className="peakDatesTable">
                      <BlackoutDates
                        blackoutDates={selectedLocation.blackoutDates}
                      />
                    </div>
                  </div>
                  <div className="mapTable">
                    <Map location={selectedLocation} />
                  </div>
                </div>
              </div>
            ) : null}

            <div className="info">
              <p>
                Peak rental periods vary by location and time. If your rental
                does not fall on peak rental dates, standard reward point values
                will be displayed during booking. To view a specific location,
                please select a location above. To view international peak
                rental periods,{" "}
                <font
                  style={{ color: "#0000EE", cursor: "pointer" }}
                  onClick={() =>
                    window.open(
                      "https://www.hertz.com/rentacar/misc/index.jsp?targetPage=2019_Anyday_Europe_Rewards.jsp",
                      "_blank"
                    )
                  }
                >
                  click here.
                </font>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
